import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Toolbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RestoreFromTrash as TrashIcon,
  Assignment as AssignIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import StudentService from '../../services/student.service';
import AsignmentService from '../../services/asignment.service';
import GradeService from '../../services/grade.service';
import CycleService from '../../services/cycle.service';
import { useTranslation } from '../../hooks/useTranslation';
const StudentList = () => {
  const { hasPermission } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [assignDialog, setAssignDialog] = useState({ open: false, student: null });
  const [bulkAssignDialog, setBulkAssignDialog] = useState({ open: false });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [assignFormData, setAssignFormData] = useState({ grade_id: '', cycle_id: '' });
  const [bulkAssignFormData, setBulkAssignFormData] = useState({ grade_id: '', cycle_id: '' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  useEffect(() => {
    fetchStudents();
    loadGradesAndCycles();
  }, []);

  const loadGradesAndCycles = async () => {
    try {
      const [gradesRes, cyclesRes] = await Promise.all([
        GradeService.getAll(),
        CycleService.getAll(),
      ]);
      setGrades(gradesRes.data?.data || []);
      setCycles(cyclesRes.data?.data || []);
    } catch (error) {
      console.error('Error loading grades and cycles:', error);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await StudentService.getAll();
      setStudents(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      showSnackbar(t('students.errorLoadingStudents') + ': ' + (error.response?.data?.message || error.message), 'error');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const handleAssignGrade = (student) => {
    setSelectedStudent(student);
    setAssignFormData({ grade_id: '', cycle_id: '' });
    setAssignDialog({ open: true, student });
    setAnchorEl(null);
  };

  const handleMenuOpen = (event, student) => {
    setAnchorEl(event.currentTarget);
    setSelectedStudent(student);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStudent(null);
  };

  const handleAssignSubmit = async () => {
    if (!assignFormData.grade_id || !assignFormData.cycle_id) {
      showSnackbar(t('students.selectBothGradeCycle'), 'error');
      return;
    }

    try {
      await AsignmentService.create({
        student_id: selectedStudent.id,
        grade_id: assignFormData.grade_id,
        cycle_id: assignFormData.cycle_id,
      });
      showSnackbar(t('students.gradeAssignedSuccess'));
      setAssignDialog({ open: false, student: null });
      setAssignFormData({ grade_id: '', cycle_id: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.message || t('students.errorAssigningGrade');
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  const handleBulkAssign = () => {
    if (selectedStudents.length === 0) {
      showSnackbar(t('students.selectAtLeastOne'), 'error');
      return;
    }
    setBulkAssignFormData({ grade_id: '', cycle_id: '' });
    setBulkAssignDialog({ open: true });
  };

  const handleBulkAssignSubmit = async () => {
    if (!bulkAssignFormData.grade_id || !bulkAssignFormData.cycle_id) {
      showSnackbar(t('students.selectBothGradeCycle'), 'error');
      return;
    }

    if (selectedStudents.length === 0) {
      showSnackbar(t('students.selectAtLeastOne'), 'error');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      const promises = selectedStudents.map(studentId =>
        AsignmentService.create({
          student_id: studentId,
          grade_id: bulkAssignFormData.grade_id,
          cycle_id: bulkAssignFormData.cycle_id,
        }).then(() => {
          successCount++;
        }).catch(() => {
          errorCount++;
        })
      );

      await Promise.all(promises);

      if (errorCount === 0) {
        showSnackbar(t('students.gradeAssignedBulkSuccess', { count: successCount }));
      } else {
        showSnackbar(t('students.assignedWithErrors', { successCount, errorCount }), 'warning');
      }

      setBulkAssignDialog({ open: false });
      setBulkAssignFormData({ grade_id: '', cycle_id: '' });
      setSelectedStudents([]);
    } catch (error) {
      showSnackbar(t('students.errorAssigningGrades'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await StudentService.delete(deleteDialog.id);
      showSnackbar(t('students.studentDeletedSuccess'));
      fetchStudents();
    } catch (error) {
      showSnackbar(t('students.errorDeletingStudent'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredStudents = students.filter(student =>
    student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('students.title')}</Typography>
            <Box display="flex" gap={1}>
              {hasPermission('students.view') && (
                <Button
                  variant="outlined"
                  startIcon={<TrashIcon />}
                  onClick={() => navigate('/students/deleted')}
                >
                  {t('students.deletedStudents')}
                </Button>
              )}
              {selectedStudents.length > 0 && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AssignIcon />}
                  onClick={handleBulkAssign}
                >
                  {t('students.bulkAssignGrade')} ({selectedStudents.length})
                </Button>
              )}
              {hasPermission('students.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/students/new')}
                >
                  {t('students.newStudent')}
                </Button>
              )}
            </Box>
          </Box>

          <TextField
            placeholder={t('students.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedStudents.length > 0 && selectedStudents.length < filteredStudents.length}
                      checked={filteredStudents.length > 0 && selectedStudents.length === filteredStudents.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>{t('students.code')}</TableCell>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('students.grade')}</TableCell>
                  <TableCell>{t('students.cycle')}</TableCell>
                  <TableCell>{t('students.createdAt')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {t('students.noStudentsFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStudents.map((student) => (
                    <TableRow 
                      key={student.id} 
                      hover
                      selected={selectedStudents.includes(student.id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => handleSelectStudent(student.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={student.code} color="primary" variant="outlined" size="small" />
                      </TableCell>
                      <TableCell>{student.name}</TableCell>
                      <TableCell>
                        {student.asignments?.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {[...new Set(student.asignments.map((a) => a.grade?.name).filter(Boolean))].map((name) => (
                              <Chip key={name} label={name} color="secondary" variant="outlined" size="small" />
                            ))}
                          </Box>
                        ) : (
                          <Chip label="—" size="small" variant="outlined" sx={{ opacity: 0.7 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        {student.asignments?.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {[...new Set(student.asignments.map((a) => a.cycle?.name).filter(Boolean))].map((name) => (
                              <Chip key={name} label={name} color="success" variant="outlined" size="small" />
                            ))}
                          </Box>
                        ) : (
                          <Chip label="—" size="small" variant="outlined" sx={{ opacity: 0.7 }} />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(student.created_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('students.edit') && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/students/${student.id}/edit`)}
                              title={t('common.edit')}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {hasPermission('students.delete') && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(student.id)}
                              title={t('common.delete')}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => handleMenuOpen(e, student)}
                            title={t('students.moreActions')}
                          >
                            <AssignIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>{t('students.deleteStudentTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('students.deleteStudentConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignDialog.open}
        onClose={() => setAssignDialog({ open: false, student: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('students.assignGradeTitle', { name: selectedStudent?.name })}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <FormControl fullWidth required>
              <InputLabel>{t('students.grade')}</InputLabel>
              <Select
                value={assignFormData.grade_id}
                label={t('students.grade')}
                onChange={(e) => setAssignFormData({ ...assignFormData, grade_id: e.target.value })}
              >
                {grades.map((grade) => (
                  <MenuItem key={grade.id} value={grade.id}>
                    {grade.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>{t('students.cycle')}</InputLabel>
              <Select
                value={assignFormData.cycle_id}
                label={t('students.cycle')}
                onChange={(e) => setAssignFormData({ ...assignFormData, cycle_id: e.target.value })}
              >
                {cycles.map((cycle) => (
                  <MenuItem key={cycle.id} value={cycle.id}>
                    {cycle.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false, student: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleAssignSubmit} variant="contained" color="primary">
            {t('students.assignGrade')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkAssignDialog.open}
        onClose={() => setBulkAssignDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('students.bulkAssignGradeTitle', { count: selectedStudents.length })}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <FormControl fullWidth required>
              <InputLabel>{t('students.grade')}</InputLabel>
              <Select
                value={bulkAssignFormData.grade_id}
                label={t('students.grade')}
                onChange={(e) => setBulkAssignFormData({ ...bulkAssignFormData, grade_id: e.target.value })}
              >
                {grades.map((grade) => (
                  <MenuItem key={grade.id} value={grade.id}>
                    {grade.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>{t('students.cycle')}</InputLabel>
              <Select
                value={bulkAssignFormData.cycle_id}
                label={t('students.cycle')}
                onChange={(e) => setBulkAssignFormData({ ...bulkAssignFormData, cycle_id: e.target.value })}
              >
                {cycles.map((cycle) => (
                  <MenuItem key={cycle.id} value={cycle.id}>
                    {cycle.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {t('students.bulkAssignDescription', { count: selectedStudents.length })}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAssignDialog({ open: false })}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleBulkAssignSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {t('students.assignGradeToAll')}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleAssignGrade(selectedStudent);
        }}>
          <ListItemIcon>
            <AssignIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('students.assignGrade')}</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentList;
